import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { defaults, defaultsDeep } from 'lodash';

import {
  ProposalHomeActions,
  ProposalStepActions,
} from '@lockton-apps/proposals/action';
import { ProposalsApiActions } from '@lockton-apps/shared/action/proposals';
import { Proposal } from '@lockton-apps/shared/interfaces';
import { QuotesApiActions } from '@lockton-apps/shared/action/quotes';

export const SHARED_PROPOSALS_FEATURE_KEY = 'shared-proposals';

export interface SharedProposalsState extends EntityState<Proposal> {
  loaded: boolean;
  updating: boolean;
  error?: string | null;
}

export interface SharedProposalsPartialState {
  readonly [SHARED_PROPOSALS_FEATURE_KEY]: SharedProposalsState;
}

export const proposalsAdapter: EntityAdapter<Proposal> =
  createEntityAdapter<Proposal>();

export const initialProposalsState: SharedProposalsState =
  proposalsAdapter.getInitialState({
    loaded: false,
    updating: false,
  });

const reducer = createReducer(
  initialProposalsState,
  on(
    ProposalStepActions.submitProposal,
    ProposalStepActions.saveProposal,
    ProposalStepActions.submitStep,
    (state) => ({
      ...state,
      updating: true,
    })
  ),
  on(
    ProposalsApiActions.beginProposalSuccess,
    ProposalsApiActions.loadProposalSuccess,
    ProposalsApiActions.saveProposalSuccess,
    ProposalsApiActions.submitStepSuccess,
    (state, { proposal }) =>
      proposalsAdapter.upsertOne(proposal, {
        ...state,
        loaded: true,
        updating: false,
        error: null,
      })
  ),
  on(
    QuotesApiActions.proposalUpdatedSuccess,
    QuotesApiActions.proposalBackupSavedSuccess,
    QuotesApiActions.loadQuoteSuccess,
    QuotesApiActions.reconfigured2Success,
    (state, { quote }) =>
      quote.proposal
        ? proposalsAdapter.upsertOne(quote.proposal, {
            ...state,
            loaded: true,
            updating: false,
            error: null,
          })
        : state
  ),
  on(
    ProposalStepActions.additionalProductsSet,
    (state, { proposalId, application }) =>
      proposalsAdapter.mapOne(
        {
          id: proposalId,
          map: (proposal) => ({
            ...proposal,
            formData: defaults({}, application, proposal.formData),
          }),
        },
        state
      )
  ),
  on(ProposalHomeActions.stepsRebuilt, (state, { proposalId, steps }) =>
    proposalsAdapter.mapOne(
      {
        id: proposalId,
        map: (proposal) => ({
          ...proposal,
          formData: defaultsDeep({ steps }, proposal.formData),
        }),
      },
      state
    )
  ),
  on(
    ProposalsApiActions.beginProposalFailure,
    ProposalsApiActions.loadProposalFailure,
    ProposalsApiActions.saveProposalFailure,
    ProposalsApiActions.submitProposalFailure,
    ProposalsApiActions.submitStepFailure,
    QuotesApiActions.proposalSubmittedFailure,
    QuotesApiActions.proposalUpdatedFailure,
    QuotesApiActions.proposalAutoSavedFailure,
    QuotesApiActions.proposalBackupSavedFailure,
    (state, { error }) => ({
      ...state,
      error,
      updating: false,
    })
  )
);

export function sharedProposalsReducer(
  state: SharedProposalsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
